/** Encuesta — IDs y opciones según especificación (sin H1 en UI). */

export type ProfileCode = "p1" | "p2" | "p2n" | "p3";

export type FieldType = "single" | "multi" | "text_short" | "text_long";

export type SurveyFieldDef = {
  id: string;
  type: FieldType;
  label: string;
  labelNeutral?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  optional?: boolean;
  visibleIf?: (v: Record<string, unknown>) => boolean;
};

export type SurveyStepDef = { id: string; fields: SurveyFieldDef[] };
export type QuestionMoment =
  | "identificacion"
  | "dolor"
  | "workaround"
  | "wtp"
  | "objeciones"
  | "lead"
  | "prioridad";

/** Intro (antes del screener), sin título H1. */
export const SURVEY_INTRO_LINES = [
  "Estamos haciendo una encuesta breve (menos de 10 minutos) para entender cómo se organizan y se comunican familias con rutinas entre hogares.",
  "Es anónima: no pedimos nombres ni datos personales. Podés saltear preguntas opcionales.",
  "Gracias por participar.",
] as const;

export const Q0_MICROCOPY =
  "Es solo para mostrarte preguntas relevantes.";

export const Q0_FIELD: SurveyFieldDef = {
  id: "q0_profile",
  type: "single",
  label: "¿Cuál de estas opciones se parece más a tu situación actual?",
  options: [
    { value: "two_homes", label: "Los chicos pasan tiempo en dos hogares" },
    { value: "one_home", label: "Vivimos en un solo hogar" },
    { value: "professional", label: "Acompaño a familias por mi trabajo (profesional)" },
    { value: "prefer_not", label: "Prefiero no decir" },
  ],
};

const P2_STEPS: SurveyStepDef[] = [
  {
    id: "s1",
    fields: [
      {
        id: "q1_friction_freq",
        type: "single",
        label:
          "En los últimos 30 días, ¿con qué frecuencia hubo fricción al coordinar o comunicarse?",
        labelNeutral:
          "En los últimos 30 días, ¿con qué frecuencia hubo fricción al coordinar o comunicarse?",
        options: [
          { value: "nunca", label: "Nunca" },
          { value: "once", label: "1 vez" },
          { value: "two_three", label: "2–3 veces" },
          { value: "weekly_once", label: "1 vez por semana" },
          { value: "weekly_several", label: "Varias veces por semana" },
          { value: "almost_daily", label: "Casi todos los días" },
        ],
      },
    ],
  },
  {
    id: "s2",
    fields: [
      {
        id: "q2_time_week",
        type: "single",
        label:
          "Aproximadamente, ¿cuánto tiempo por semana te lleva coordinar (mensajes, cambios, recordatorios)?",
        labelNeutral:
          "Aproximadamente, ¿cuánto tiempo por semana te lleva coordinar (mensajes, cambios, recordatorios)?",
        options: [
          { value: "lt15m", label: "<15m" },
          { value: "m15_30", label: "15–30m" },
          { value: "m30_60", label: "30–60m" },
          { value: "h1_2", label: "1–2h" },
          { value: "h2_4", label: "2–4h" },
          { value: "gt4h", label: ">4h" },
        ],
      },
    ],
  },
  {
    id: "s3",
    fields: [
      {
        id: "q3_consequence",
        type: "multi",
        min: 1,
        label:
          "En los últimos 30 días, ¿qué pasó como consecuencia de esa fricción? (elegí todas las que apliquen)",
        labelNeutral:
          "En los últimos 30 días, ¿qué pasó como consecuencia de esa fricción? (elegí todas las que apliquen)",
        options: [
          { value: "plan_change", label: "Hubo cambios de plan a último momento" },
          { value: "info_lost", label: "Se perdió información (horarios, indicaciones, cosas)" },
          { value: "arguments", label: "Hubo discusiones por mensajes" },
          { value: "stopped_reply", label: "Se dejó de responder por un tiempo" },
          {
            value: "third_party",
            label: "Se terminó coordinando por un tercero (familiar/profesional)",
          },
          { value: "stress", label: "Generó estrés / me dejó mal el día" },
          { value: "nothing", label: "Nada de lo anterior" },
        ],
      },
    ],
  },
  {
    id: "s4",
    fields: [
      {
        id: "q4_situation",
        type: "multi",
        min: 1,
        max: 4,
        label:
          "¿En qué tipo de situaciones se concentra más la coordinación? (elegí todas las que apliquen)",
        labelNeutral:
          "¿En qué tipo de situaciones se concentra más la coordinación? (elegí todas las que apliquen)",
        options: [
          { value: "transfers", label: "Horarios y traslados" },
          { value: "school", label: "Escuela" },
          { value: "health_daily", label: "Salud cotidiana" },
          { value: "holidays", label: "Vacaciones/feriados/viajes" },
          { value: "expenses", label: "Gastos cotidianos" },
          { value: "other", label: "Otra" },
        ],
      },
      {
        id: "q4b_other_text",
        type: "text_short",
        maxLength: 60,
        optional: true,
        label: "¿Cuál?",
        visibleIf: (v) =>
          Array.isArray(v.q4_situation) &&
          (v.q4_situation as string[]).includes("other"),
      },
    ],
  },
  {
    id: "s5",
    fields: [
      {
        id: "q5_channels",
        type: "multi",
        min: 1,
        label: "¿Por qué canales se coordinan hoy? (elegí todos los que apliquen)",
        labelNeutral: "¿Por qué canales se usan hoy para coordinar? (elegí todos los que apliquen)",
        options: [
          { value: "whatsapp", label: "WhatsApp/mensajes" },
          { value: "calls", label: "Llamadas" },
          { value: "email", label: "Email" },
          { value: "calendar", label: "Calendario compartido" },
          { value: "notes", label: "Notas/cuaderno" },
          { value: "org_app", label: "App de organización" },
          { value: "third_party", label: "Por un tercero" },
          { value: "other_ch", label: "Otro" },
        ],
      },
      {
        id: "q5b_app_name",
        type: "text_short",
        maxLength: 60,
        optional: true,
        label: "¿Cuál app?",
        visibleIf: (v) =>
          Array.isArray(v.q5_channels) && (v.q5_channels as string[]).includes("org_app"),
      },
    ],
  },
  {
    id: "s6",
    fields: [
      {
        id: "q6_what_breaks",
        type: "multi",
        min: 1,
        max: 4,
        label:
          "En los últimos 30 días, ¿qué fue lo más común que falló? (elegí todas las que apliquen)",
        options: [
          { value: "mix_threads", label: "Se mezclan temas y se vuelve difícil seguir el hilo" },
          { value: "lost_msgs", label: "Se pierden mensajes o acuerdos" },
          { value: "no_confirm", label: "No hay confirmación clara de que se vio/entendió" },
          { value: "stops_coord", label: "Se interrumpe la coordinación (se deja de responder)" },
          {
            value: "messy_find",
            label: "Cuesta encontrar información después (queda todo desordenado)",
          },
          { value: "other", label: "Otro" },
        ],
      },
      {
        id: "q6b_break_other",
        type: "text_short",
        maxLength: 80,
        optional: true,
        label: "¿Qué falló?",
        visibleIf: (v) =>
          Array.isArray(v.q6_what_breaks) &&
          (v.q6_what_breaks as string[]).includes("other"),
      },
    ],
  },
  {
    id: "s7",
    fields: [
      {
        id: "q7_urgency",
        type: "single",
        label: "¿Qué tan pronto te gustaría que esto mejore?",
        options: [
          { value: "next_week", label: "próxima semana" },
          { value: "next_month", label: "próximo mes" },
          { value: "three_months", label: "próximos 3 meses" },
          { value: "not_priority", label: "no es prioridad ahora" },
        ],
      },
    ],
  },
  {
    id: "s8",
    fields: [
      {
        id: "q8_spend_now",
        type: "single",
        optional: true,
        label:
          "En los últimos 3 meses, ¿pagaste algo para organizar o mejorar la coordinación (herramientas, servicios, suscripciones)? (opcional)",
        options: [
          { value: "none", label: "No pagué nada" },
          { value: "lt5", label: "<USD5/mes" },
          { value: "usd5_10", label: "USD5–10" },
          { value: "usd10_20", label: "USD10–20" },
          { value: "gt20", label: ">USD20" },
          { value: "prefer_not", label: "Prefiero no decir" },
        ],
      },
      {
        id: "q9_price_sense",
        type: "single",
        optional: true,
        label:
          "Pensando en tu situación, para una herramienta que te ahorre tiempo y reduzca malentendidos… ¿qué te parecería caro por mes? (opcional)",
        options: [
          { value: "gt5", label: ">USD5" },
          { value: "gt10", label: ">USD10" },
          { value: "gt20", label: ">USD20" },
          { value: "gt40", label: ">USD40" },
          { value: "depends", label: "No sabría/depende" },
          { value: "prefer_not", label: "Prefiero no decir" },
        ],
      },
    ],
  },
  {
    id: "s9",
    fields: [
      {
        id: "q10_objections",
        type: "multi",
        min: 1,
        max: 2,
        label: "¿Qué te frenaría más para usar una herramienta así? (elegí hasta 2)",
        options: [
          { value: "other_wont_use", label: "Que la otra persona no la use" },
          { value: "privacy", label: "Privacidad / no quiero dejar registro" },
          { value: "no_another_app", label: "No quiero otra app más" },
          { value: "complicated", label: "Me preocupa que sea complicada" },
          { value: "other_way", label: "Prefiero resolverlo de otra manera" },
          { value: "other_obj", label: "Otro" },
        ],
      },
      {
        id: "q10b_obj_other",
        type: "text_short",
        maxLength: 80,
        optional: true,
        label: "¿Qué te frenaría?",
        visibleIf: (v) =>
          Array.isArray(v.q10_objections) &&
          (v.q10_objections as string[]).includes("other_obj"),
      },
    ],
  },
  {
    id: "s10",
    fields: [
      {
        id: "q11_commitment",
        type: "single",
        label:
          "Si existiera una herramienta para probar con datos de ejemplo por 10 minutos, ¿te interesaría?",
        options: [
          { value: "yes", label: "Sí" },
          { value: "no", label: "No" },
          { value: "maybe", label: "Tal vez" },
        ],
      },
    ],
  },
];

const P1_STEPS: SurveyStepDef[] = [
  {
    id: "p1s1",
    fields: [
      {
        id: "p1q1_general_org",
        type: "single",
        label: "¿Cuánto tiempo por semana les lleva coordinar agenda familiar?",
        options: [
          { value: "lt30", label: "<30m" },
          { value: "m30_60", label: "30–60m" },
          { value: "h1_2", label: "1–2h" },
          { value: "gt2", label: ">2h" },
        ],
      },
    ],
  },
  {
    id: "p1s2",
    fields: [
      {
        id: "p1q2_top_issue",
        type: "multi",
        min: 1,
        max: 2,
        label: "¿Qué se les complica más?",
        options: [
          { value: "agenda", label: "Agenda" },
          { value: "school", label: "Escuela" },
          { value: "health", label: "Salud cotidiana" },
          { value: "expenses", label: "Gastos" },
          { value: "other", label: "Otra" },
        ],
      },
    ],
  },
];

const P3_STEPS: SurveyStepDef[] = [
  {
    id: "p3s1",
    fields: [
      {
        id: "p3q1_cases",
        type: "single",
        label: "En promedio, ¿cuántos casos activos acompañás por mes?",
        options: [
          { value: "c1_2", label: "1–2" },
          { value: "c3_5", label: "3–5" },
          { value: "c6_10", label: "6–10" },
          { value: "c11_20", label: "11–20" },
          { value: "c_gt20", label: "Más de 20" },
          { value: "prefer_not", label: "Prefiero no decir" },
        ],
      },
    ],
  },
  {
    id: "p3s2",
    fields: [
      {
        id: "p3q2_time",
        type: "single",
        label: "¿Cuánto tiempo te insume por semana ordenar comunicación/coordinar?",
        options: [
          { value: "lt30", label: "<30m" },
          { value: "m30_60", label: "30–60m" },
          { value: "h1_2", label: "1–2h" },
          { value: "h2_4", label: "2–4h" },
          { value: "gt4h", label: ">4h" },
          { value: "depends", label: "Depende demasiado" },
        ],
      },
    ],
  },
  {
    id: "p3s3",
    fields: [
      {
        id: "p3q3_evidence",
        type: "multi",
        min: 1,
        label: "¿Cómo te llega hoy la información cuando hay malentendidos?",
        options: [
          { value: "screenshots", label: "Capturas" },
          { value: "chats", label: "Chats exportados" },
          { value: "emails", label: "Emails" },
          { value: "audio", label: "Audios" },
          { value: "session", label: "Relatos en sesión" },
          { value: "other", label: "Otro" },
        ],
      },
    ],
  },
  {
    id: "p3s4",
    fields: [
      {
        id: "p3q4_pain",
        type: "single",
        label: "¿Qué es lo que más complica el trabajo?",
        options: [
          { value: "messy", label: "Desorden" },
          { value: "mix_topics", label: "Se mezclan temas" },
          { value: "no_read_confirm", label: "Falta confirmación lectura" },
          { value: "stops", label: "Se corta coordinación" },
          { value: "other", label: "Otro" },
        ],
      },
    ],
  },
  {
    id: "p3s5",
    fields: [
      {
        id: "p3q5_situations",
        type: "multi",
        min: 1,
        label: "¿En qué situaciones ves más fricción?",
        options: [
          { value: "transfers", label: "Horarios/traslados" },
          { value: "school", label: "Escuela" },
          { value: "health", label: "Salud cotidiana" },
          { value: "holidays", label: "Vacaciones/feriados" },
          { value: "expenses", label: "Gastos cotidianos" },
          { value: "other", label: "Otra" },
        ],
      },
    ],
  },
  {
    id: "p3s6",
    fields: [
      {
        id: "p3q6_musts",
        type: "multi",
        min: 1,
        max: 3,
        label: "¿Qué mínimos debería tener una herramienta para que la recomiendes? (hasta 3)",
        options: [
          { value: "channels_topic", label: "Canales por tema" },
          { value: "calendar", label: "Calendario" },
          { value: "export_history", label: "Historial exportable" },
          { value: "read_confirm", label: "Confirmación lectura" },
          { value: "roles", label: "Permisos por rol" },
          { value: "easy", label: "Fácil de usar" },
          { value: "other", label: "Otro" },
        ],
      },
    ],
  },
  {
    id: "p3s7",
    fields: [
      {
        id: "p3q7_payment_model",
        type: "single",
        label:
          "Hoy, cuando recomendás herramientas/servicios de organización, ¿cómo se paga típicamente?",
        options: [
          { value: "client_pays", label: "lo paga el cliente" },
          { value: "bundled", label: "se incluye en servicio" },
          { value: "suggest_rare", label: "se sugiere pero rara vez se paga" },
          { value: "no_recommend", label: "no recomiendo" },
          { value: "other_pay", label: "otro" },
        ],
      },
    ],
  },
  {
    id: "p3s8",
    fields: [
      {
        id: "p3q8_commitment",
        type: "single",
        label: "¿Aceptarías evaluar una herramienta con 1–3 familias y dar feedback?",
        options: [
          { value: "yes", label: "Sí" },
          { value: "no", label: "No" },
          { value: "maybe", label: "Tal vez" },
        ],
      },
    ],
  },
];

export function profileFromQ0(v: string): ProfileCode {
  if (v === "one_home") return "p1";
  if (v === "professional") return "p3";
  if (v === "prefer_not") return "p2n";
  return "p2";
}

export function stepsForProfile(p: ProfileCode): SurveyStepDef[] {
  if (p === "p1") return P1_STEPS;
  if (p === "p3") return P3_STEPS;
  return P2_STEPS;
}

export function fieldText(f: SurveyFieldDef, neutral: boolean): string {
  if (neutral && f.labelNeutral) return f.labelNeutral;
  return f.label;
}

export function questionLabelCatalog(): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = [];
  const seen = new Set<string>();
  const push = (id: string, label: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ id, label });
  };

  push(Q0_FIELD.id, Q0_FIELD.label);
  for (const step of [...P1_STEPS, ...P2_STEPS, ...P3_STEPS]) {
    for (const f of step.fields) {
      push(f.id, f.label);
    }
  }

  push(
    "q12_open",
    "Si pudieras hacer que una sola cosa sea más fácil en la coordinación, ¿cuál sería? (opcional)",
  );
  push("p1q3_open", "¿Qué les gustaría que fuera más fácil? (opcional)");
  push(
    "optin_email",
    "Si querés recibir un resumen de resultados o participar de una prueba guiada, dejá un email (opcional).",
  );
  push(
    "consent",
    "Entiendo que los datos son anónimos y no incluyen información sensible de menores; acepto participar.",
  );

  return out;
}

export function questionEditorCatalog(): {
  id: string;
  label: string;
  moment: QuestionMoment;
  options: { value: string; label: string }[];
}[] {
  const out: {
    id: string;
    label: string;
    moment: QuestionMoment;
    options: { value: string; label: string }[];
  }[] = [];
  const seen = new Set<string>();
  const add = (
    id: string,
    label: string,
    moment: QuestionMoment,
    options?: { value: string; label: string }[],
  ) => {
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ id, label, moment, options: options ?? [] });
  };

  add(Q0_FIELD.id, Q0_FIELD.label, "identificacion", Q0_FIELD.options);

  const byId: Record<string, QuestionMoment> = {
    q1_friction_freq: "dolor",
    q2_time_week: "dolor",
    q3_consequence: "dolor",
    q4_situation: "dolor",
    q4b_other_text: "dolor",
    q5_channels: "workaround",
    q5b_app_name: "workaround",
    q6_what_breaks: "workaround",
    q6b_break_other: "workaround",
    q7_urgency: "prioridad",
    q8_spend_now: "wtp",
    q9_price_sense: "wtp",
    q10_objections: "objeciones",
    q10b_obj_other: "objeciones",
    q11_commitment: "lead",
    q12_open: "lead",
    p1q1_general_org: "dolor",
    p1q2_top_issue: "dolor",
    p1q3_open: "lead",
    p3q1_cases: "identificacion",
    p3q2_time: "dolor",
    p3q3_evidence: "workaround",
    p3q4_pain: "dolor",
    p3q5_situations: "dolor",
    p3q6_musts: "workaround",
    p3q7_payment_model: "wtp",
    p3q8_commitment: "lead",
    optin_email: "lead",
    consent: "lead",
  };

  const allFields: SurveyFieldDef[] = [
    ...P1_STEPS.flatMap((s) => s.fields),
    ...P2_STEPS.flatMap((s) => s.fields),
    ...P3_STEPS.flatMap((s) => s.fields),
  ];
  for (const f of allFields) {
    add(f.id, f.label, byId[f.id] ?? "dolor", f.options);
  }

  add(
    "q12_open",
    "Si pudieras hacer que una sola cosa sea más fácil en la coordinación, ¿cuál sería? (opcional)",
    "lead",
  );
  add("p1q3_open", "¿Qué les gustaría que fuera más fácil? (opcional)", "lead");
  add(
    "optin_email",
    "Si querés recibir un resumen de resultados o participar de una prueba guiada, dejá un email (opcional).",
    "lead",
  );
  add(
    "consent",
    "Entiendo que los datos son anónimos y no incluyen información sensible de menores; acepto participar.",
    "lead",
  );

  return out;
}
